
import { getDb } from '../db/db';
import { StockMovement } from '../../types';
import { v4 as uuidv4 } from 'uuid';

export const stockMovementRepository = {
    async get(id: string): Promise<StockMovement | undefined> {
        const db = await getDb();
        return db.get('stock_movements_v1', id);
    },

    async findByExternalRef(externalRef: string): Promise<StockMovement | undefined> {
        const db = await getDb();
        return db.getFromIndex('stock_movements_v1', 'by-externalRef', externalRef);
    },

    async findByDocument(documentType: string, documentId: string): Promise<StockMovement[]> {
        const db = await getDb();
        return db.getAllFromIndex('stock_movements_v1', 'by-document', [documentType, documentId]);
    },

    async findByLocation(locationId: string): Promise<StockMovement[]> {
        const db = await getDb();
        // In multi-user we usually search by stockLocationId OR from/to.
        // Here we might need improved querying.
        // For now, let's just get everything relevant.
        // Note: IndexedDB doesn't support "OR" queries easily.
        // We can do parallel queries.
        const [from, to] = await Promise.all([
            // If we strictly use from/to for transfers, direct might be empty or used for adjustments.
            // But let's check all fields just in case implementation varies.
            // Actually, db.ts schema does not have 'by-stockLocationId'. It only has 'by-from', 'by-to'.
            // Wait, did I omit `by-stockLocationId` in db.ts?
            // Let's check db definition in thought process.
            // I put 'by-from', 'by-to', 'by-document'.
            // I did NOT put 'by-stockLocationId'.
            // So I must rely on from/to.
            db.getAllFromIndex('stock_movements_v1', 'by-from', locationId),
            db.getAllFromIndex('stock_movements_v1', 'by-to', locationId)
        ]);

        // Merge and deduplicate
        const map = new Map<string, StockMovement>();
        [...from, ...to].forEach(m => map.set(m.id, m));
        return Array.from(map.values());
    },

    async save(movement: StockMovement): Promise<void> {
        const db = await getDb();
        await db.put('stock_movements_v1', movement);
    },

    async create(data: Omit<StockMovement, 'id' | 'occurredAt'> & { occurredAt?: string }): Promise<StockMovement> {
        const movement: StockMovement = {
            occurredAt: new Date().toISOString(),
            ...data,
            id: uuidv4(),
        };
        await this.save(movement);
        return movement;
    }
};
